import type { AccountDeletion } from '@nebueye/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { Logo } from '@/components/brand/logo'
import { Button } from '@/components/ui/button'
import { apiFetch } from '@/lib/api'
import { authClient } from '@/lib/auth-client'
import { sessionQuery } from '@/lib/session'

/**
 * Where a signed-in user lands while their account is scheduled for deletion
 * (every data route refuses them until it is restored). The one job of this
 * page: make keeping the account a single click.
 */
export const Route = createFileRoute('/account-deletion')({
  beforeLoad: async ({ context }) => {
    const session = await context.queryClient.fetchQuery(sessionQuery).catch(() => null)
    if (!session) throw redirect({ to: '/sign-in' })
    if (!session.user.deletionScheduledFor) throw redirect({ to: '/' })
    return { session }
  },
  head: () => ({ meta: [{ title: 'Account scheduled for deletion · Nebueye' }] }),
  component: AccountDeletionPage,
})

function AccountDeletionPage() {
  const { session } = Route.useRouteContext()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const plan = useQuery({ queryKey: ['account-deletion'], queryFn: () => apiFetch<AccountDeletion>('/account/deletion') })
  const when = new Date(session.user.deletionScheduledFor!).toLocaleDateString(undefined, { dateStyle: 'long' })
  const restore = useMutation({
    mutationFn: () => apiFetch<AccountDeletion>('/account/restore', { method: 'POST' }),
    onSuccess: async () => {
      queryClient.clear()
      toast.success('Welcome back. Your account stays.')
      navigate({ to: '/' })
    },
    onError: (e) => toast.error(e.message),
  })
  const names = (ws: { name: string }[]) => ws.map((w) => w.name).join(', ')

  return (
    <div className="flex min-h-dvh flex-col px-6 py-8 sm:px-12">
      <Logo />
      <main className="flex flex-1 items-center py-12">
        <div className="animate-in fade-in slide-in-from-bottom-2 mx-auto w-full max-w-[440px] space-y-6 duration-500 ease-out">
          <div className="space-y-2">
            <p className="text-status-danger-text text-sm font-medium">Scheduled for deletion</p>
            <h1 className="text-headline font-semibold tracking-tight text-balance">Your account is deleted on {when}</h1>
            <p className="text-muted-foreground text-[15px] leading-6">
              Until then nothing is removed, and signing in here is all it takes to keep it. Your other devices were signed out when you asked for this.
            </p>
          </div>
          {plan.data && (plan.data.workspacesToDelete.length > 0 || plan.data.workspacesToLeave.length > 0) && (
            <ul className="text-muted-foreground space-y-1.5 text-sm">
              {plan.data.workspacesToDelete.length > 0 && (
                <li>
                  <span className="text-foreground font-medium">Deleted with all their orders:</span> {names(plan.data.workspacesToDelete)}
                </li>
              )}
              {plan.data.workspacesToLeave.length > 0 && (
                <li>
                  <span className="text-foreground font-medium">You leave, the others keep their data:</span> {names(plan.data.workspacesToLeave)}
                </li>
              )}
            </ul>
          )}
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => restore.mutate()} disabled={restore.isPending}>
              {restore.isPending && <Loader2 className="animate-spin" />}
              Keep my account
            </Button>
            <Button
              variant="ghost"
              onClick={async () => {
                await authClient.signOut()
                queryClient.clear()
                navigate({ to: '/sign-in' })
              }}
            >
              Sign out
            </Button>
          </div>
        </div>
      </main>
      <p className="text-subtle text-xs">© {new Date().getFullYear()} Nebueye</p>
    </div>
  )
}
